import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

/**
 * Collapsible piano keyboard for the Kontakt-style sample player.
 * Extracted from BatucadaRio.jsx.
 *
 * All keyboard state (activeNotes, keyMappings, selectedKey, isCollapsed)
 * is owned by the parent so this stays a dumb, controlled component.
 */
export const PianoKeyboard = ({
  onNoteDown,
  onNoteUp,
  activeNotes = [],
  keyMappings = {},
  onEditKey,
  selectedKey,
  isCollapsed,
  onToggleCollapse,
}) => {
  const whiteNotes = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
  const blackNotes = ['C#', 'D#', null, 'F#', 'G#', 'A#', null];
  const octaves = [2, 3, 4, 5, 6];

  const handleKeyDown = (note, octave, e) => {
    e.preventDefault();
    const fullNote = `${note}${octave}`;
    onNoteDown?.(fullNote);
  };

  const handleKeyUp = (note, octave, e) => {
    e.preventDefault();
    const fullNote = `${note}${octave}`;
    onNoteUp?.(fullNote, false);
  };

  const handleKeyLeave = (note, octave, e) => {
    e.preventDefault();
    const fullNote = `${note}${octave}`;
    onNoteUp?.(fullNote, true);
  };

  const handleClick = (note, octave, e) => {
    e.preventDefault();
    const fullNote = `${note}${octave}`;
    if (keyMappings[fullNote] && onEditKey) {
      onEditKey(fullNote);
    }
  };

  // Collapsed state - thin bar
  if (isCollapsed) {
    return (
      <div
        className="h-10 bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border-t border-[#333] flex items-center justify-between px-4 cursor-pointer hover:bg-[#222]"
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-2">
          <ChevronUp className="w-4 h-4 text-gray-500" />
          <span className="text-[10px] text-gray-500">KEYBOARD</span>
        </div>
        <span className="text-[10px] text-amber-400">
          {Object.keys(keyMappings).length > 0
            ? `${Object.keys(keyMappings).length} keys mapped`
            : 'No samples loaded'}
        </span>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border-t border-[#333]">
      {/* Header with collapse button */}
      <div className="flex items-center justify-between px-3 py-1 border-b border-[#333]">
        <button
          onClick={onToggleCollapse}
          className="flex items-center gap-2 text-gray-500 hover:text-white"
        >
          <ChevronDown className="w-4 h-4" />
          <span className="text-[10px]">KEYBOARD</span>
        </button>
        <span className="text-[10px] text-gray-500">
          {Object.keys(keyMappings).length > 0
            ? `${Object.keys(keyMappings).length} keys mapped - Click a key to edit`
            : 'Select an instrument package to map keys'}
        </span>
      </div>
      {/* Piano keys */}
      <div className="h-28 flex items-end justify-center overflow-hidden pb-2">
        <div className="relative flex">
          {octaves.map((octave) => (
            <div key={octave} className="relative flex">
              {/* White keys */}
              {whiteNotes.map((note) => {
                const fullNote = `${note}${octave}`;
                const mapping = keyMappings[fullNote];
                const isActive = activeNotes.includes(fullNote);
                const isSelected = selectedKey === fullNote;
                return (
                  <button
                    key={fullNote}
                    onMouseDown={(e) => handleKeyDown(note, octave, e)}
                    onMouseUp={(e) => handleKeyUp(note, octave, e)}
                    onMouseLeave={(e) => handleKeyLeave(note, octave, e)}
                    onClick={(e) => handleClick(note, octave, e)}
                    onTouchStart={(e) => handleKeyDown(note, octave, e)}
                    onTouchEnd={(e) => handleKeyUp(note, octave, e)}
                    className={`w-8 h-24 border border-gray-300 rounded-b-sm transition-all duration-75 relative select-none flex flex-col justify-end
                      ${mapping ? 'bg-gradient-to-b from-amber-200 to-amber-300' : 'bg-gradient-to-b from-white to-gray-100'}
                      ${isActive ? 'from-amber-400 to-amber-500 scale-[0.98]' : ''}
                      ${isSelected ? 'ring-2 ring-cyan-400 ring-inset' : ''}
                      ${mapping ? 'hover:from-amber-300 hover:to-amber-400 cursor-pointer' : 'hover:from-gray-100 hover:to-gray-200'}
                      active:from-gray-200 active:to-gray-300`}
                    data-testid={`piano-key-${fullNote}`}
                    title={mapping ? `${mapping.name} (click to edit)` : fullNote}
                  >
                    {mapping && (
                      <span className="text-[6px] text-amber-800 font-medium px-0.5 pb-1 text-center leading-tight truncate">
                        {mapping.name.length > 8 ? mapping.name.slice(0, 8) : mapping.name}
                      </span>
                    )}
                  </button>
                );
              })}
              {/* Black keys */}
              <div className="absolute top-0 flex pointer-events-none" style={{ left: '20px' }}>
                {blackNotes.map((note, i) => {
                  if (!note) return <div key={i} className="w-8" />;
                  const fullNote = `${note}${octave}`;
                  const mapping = keyMappings[fullNote];
                  const isActive = activeNotes.includes(fullNote);
                  const isSelected = selectedKey === fullNote;
                  return (
                    <button
                      key={fullNote}
                      onMouseDown={(e) => { e.stopPropagation(); handleKeyDown(note, octave, e); }}
                      onMouseUp={(e) => { e.stopPropagation(); handleKeyUp(note, octave, e); }}
                      onMouseLeave={(e) => { e.stopPropagation(); handleKeyLeave(note, octave, e); }}
                      onClick={(e) => { e.stopPropagation(); handleClick(note, octave, e); }}
                      onTouchStart={(e) => { e.stopPropagation(); handleKeyDown(note, octave, e); }}
                      onTouchEnd={(e) => { e.stopPropagation(); handleKeyUp(note, octave, e); }}
                      className={`w-5 h-14 rounded-b-sm transition-all duration-75 mx-[6px] relative z-10 select-none pointer-events-auto flex flex-col justify-end
                        ${mapping ? 'bg-gradient-to-b from-amber-600 to-amber-800' : 'bg-gradient-to-b from-gray-800 to-black'}
                        ${isActive ? 'from-amber-500 to-amber-700 scale-[0.98]' : ''}
                        ${isSelected ? 'ring-2 ring-cyan-400 ring-inset' : ''}
                        ${mapping ? 'hover:from-amber-500 hover:to-amber-700 cursor-pointer' : 'hover:from-gray-700 hover:to-gray-900'}
                        active:from-gray-900 active:to-black`}
                      data-testid={`piano-key-${fullNote}`}
                      title={mapping ? `${mapping.name} (click to edit)` : fullNote}
                    >
                      {mapping && (
                        <span className="text-[5px] text-amber-200 font-medium pb-0.5 text-center truncate">
                          {mapping.name.slice(0, 4)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PianoKeyboard;
